import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { CampoData } from '../../componentes/formulario';
import type { HorarioTrabalho } from './tipos';

const ORDEM_DIAS = ['1', '2', '3', '4', '5', '6', '0'];

/** Resumo curto do expediente: "Seg a Sex" ou "Seg, Qua, Sáb". */
export function resumoExpediente(h: HorarioTrabalho, t: (k: string) => string): string {
  const dias = ORDEM_DIAS.filter((d) => (h[d]?.length ?? 0) > 0);
  if (!dias.length) return t('tabela.semExpediente');
  const idx = dias.map((d) => ORDEM_DIAS.indexOf(d));
  const seguidos = idx.every((v, i) => i === 0 || v === idx[i - 1]! + 1);
  if (dias.length > 2 && seguidos) return `${t(`diasCurto.${dias[0]}`)} a ${t(`diasCurto.${dias[dias.length - 1]}`)}`;
  return dias.map((d) => t(`diasCurto.${d}`)).join(', ');
}

/** Editor do horário de trabalho: por dia da semana, vários intervalos HH:MM. Dia sem intervalo é folga. */
export function HorarioEditor({ valor, aoMudar }: { valor: HorarioTrabalho; aoMudar: (h: HorarioTrabalho) => void }) {
  const { t } = useTranslation('agenda');
  const trocar = (dia: string, lista: { de: string; ate: string }[]) => aoMudar({ ...valor, [dia]: lista });
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium text-tinta-2">{t('campos.horarioTrabalho')}</legend>
      {ORDEM_DIAS.map((dia) => {
        const lista = valor[dia] ?? [];
        return (
          <div key={dia} className="flex flex-wrap items-start gap-x-3 gap-y-2 rounded-lg border border-borda px-3 py-2">
            <span className="w-20 shrink-0 pt-2 text-sm font-medium">{t(`dias.${dia}`)}</span>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              {lista.length === 0 && <span className="pt-2 text-sm text-tinta-3">{t('horarios.folga')}</span>}
              {lista.map((i, k) => (
                <div key={k} className="flex flex-wrap items-center gap-2">
                  <div className="w-36"><CampoData aria-label={`${t(`dias.${dia}`)} ${t('horarios.de')}`} type="time" value={i.de} onChange={(e) => trocar(dia, lista.map((x, j) => (j === k ? { ...x, de: e.target.value } : x)))} /></div>
                  <span className="text-sm text-tinta-3">{t('horarios.ate').toLowerCase()}</span>
                  <div className="w-36"><CampoData aria-label={`${t(`dias.${dia}`)} ${t('horarios.ate')}`} type="time" value={i.ate} onChange={(e) => trocar(dia, lista.map((x, j) => (j === k ? { ...x, ate: e.target.value } : x)))} /></div>
                  <Botao variante="fantasma" tamanho="sm" aria-label={t('acoes.remover')} onClick={() => trocar(dia, lista.filter((_, j) => j !== k))} icone={<Trash2 className="size-4" />} />
                </div>
              ))}
            </div>
            <Botao variante="fantasma" tamanho="sm" className="self-end" aria-label={`${t('acoes.adicionarIntervalo')} (${t(`dias.${dia}`)})`} title={t('acoes.adicionarIntervalo')} onClick={() => trocar(dia, [...lista, lista.length ? { de: '13:00', ate: '18:00' } : { de: '09:00', ate: '12:00' }])} icone={<Plus className="size-4" />} />
          </div>
        );
      })}
    </fieldset>
  );
}
